import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';
import { CategoriaProducto } from './categoriaProductoService';

const API_URL = 'productos-cafeteria/';

// ── Interfaces ──────────────────────────────────────────────

export interface SucursalInfo {
    id: number;
    nombre: string;
}

// Reutilizamos o definimos una versión ligera para el info
export interface CategoriaInfo extends CategoriaProducto {}

export interface ProductoCafeteria {
    id: number;
    nombre: string;
    descripcion: string;
    precio: string; // decimal como string desde el backend
    imagen: string | null;
    activo: boolean;
    sucursal: number;
    sucursal_info: SucursalInfo | null;
    categoria: number | null;
    categoria_info: CategoriaProducto | null;
    controlar_stock: boolean;
    stock: number | null;
}

export interface ProductoCafeteriaFormData {
    nombre: string;
    descripcion?: string;
    precio: string; // o number, pero se enviará como string en FormData
    imagen?: File | null;
    activo?: boolean;
    sucursal: number;
    categoria?: number | null;
    controlar_stock?: boolean;
    stock?: number | null;
}

// ── Peticiones ──────────────────────────────────────────────

/**
 * Obtiene los productos de cafetería con filtros opcionales.
 * @param params Filtros: sucursal_id, categoria_id, activo
 */
export const fetchProductosCafeteria = async (params?: {
    sucursal_id?: number;
    categoria_id?: number;
    activo?: boolean;
}): Promise<ProductoCafeteria[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL, { params });
        return response.data;
    });
};

/**
 * Obtiene un producto por su ID.
 */
export const fetchProductoCafeteria = async (
    id: number
): Promise<ProductoCafeteria> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/`);
        return response.data;
    });
};

/**
 * Crea un nuevo producto de cafetería (usa FormData para soportar imagen).
 * @param data Datos del producto
 */
export const createProductoCafeteria = async (
    data: ProductoCafeteriaFormData
): Promise<ProductoCafeteria> => {
    return handleRequestWithAuth(async () => {
        const formData = new FormData();

        formData.append('nombre', data.nombre);
        if (data.descripcion) formData.append('descripcion', data.descripcion);
        formData.append('precio', data.precio);
        if (data.imagen) formData.append('imagen', data.imagen);
        if (data.activo !== undefined) formData.append('activo', data.activo.toString());
        formData.append('sucursal', data.sucursal.toString());
        if (data.categoria !== undefined && data.categoria !== null) {
            formData.append('categoria', data.categoria.toString());
        }
        if (data.controlar_stock !== undefined) {
            formData.append('controlar_stock', data.controlar_stock.toString());
        }
        if (data.stock !== undefined && data.stock !== null) {
            formData.append('stock', data.stock.toString());
        }

        const response = await axiosInstance.post(API_URL, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    });
};

/**
 * Actualiza un producto de cafetería (PUT, usa FormData para mantener la imagen).
 * @param id ID del producto
 * @param data Campos a actualizar
 */
export const updateProductoCafeteria = async (
    id: number,
    data: Partial<ProductoCafeteriaFormData>
): Promise<ProductoCafeteria> => {
    return handleRequestWithAuth(async () => {
        const formData = new FormData();

        if (data.nombre) formData.append('nombre', data.nombre);
        if (data.descripcion !== undefined) formData.append('descripcion', data.descripcion);
        if (data.precio) formData.append('precio', data.precio);
        if (data.imagen !== undefined) {
            if (data.imagen) {
                formData.append('imagen', data.imagen);
            }
            // Si se envía null, se podría eliminar la imagen. Asegúrate de que el backend lo soporte.
        }
        if (data.activo !== undefined) formData.append('activo', data.activo.toString());
        if (data.sucursal !== undefined) formData.append('sucursal', data.sucursal.toString());
        if (data.categoria !== undefined) {
            formData.append('categoria', data.categoria !== null ? data.categoria.toString() : '');
        }
        if (data.controlar_stock !== undefined) {
            formData.append('controlar_stock', data.controlar_stock.toString());
        }
        if (data.stock !== undefined) {
            formData.append('stock', data.stock !== null ? data.stock.toString() : '');
        }

        const response = await axiosInstance.put(`${API_URL}${id}/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    });
};

/**
 * Elimina un producto de cafetería.
 * @param id ID del producto
 */
export const deleteProductoCafeteria = async (id: number): Promise<void> => {
    return handleRequestWithAuth(async () => {
        await axiosInstance.delete(`${API_URL}${id}/`);
    });
};